import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { CameraIcon, CheckCircleIcon, XMarkIcon, FolderIcon } from "@heroicons/react/24/outline";
import { UploaderV4 } from "./Uploaderv4";
import { TWclassNames } from "../../utils/Div";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import { ProjectFolderSelector } from "../../utils/moduleSelectors/projectFolderSelector";
import {
  bindUploadEntryToFolder,
  folderFromSelectorEvent,
  folderUploadFallback,
} from "./document-upload-service";

function formatUploadBytes(bytes) {
  const size = Number(bytes);
  if (!size || Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtLabel(filename = "") {
  const part = String(filename).split(".").pop() || "";
  return part.slice(0, 4).toUpperCase() || "FIL";
}

function rowUploadProgress(index, uploadedCount, isUploading, uploadProgress) {
  if (index < uploadedCount) return 100;
  if (index > uploadedCount) return 0;
  if (!isUploading) return 0;
  const values = Object.values(uploadProgress || {});
  const active = values.find((value) => value < 100);
  return active ?? (values.length ? 100 : 0);
}

function revokePreviewUrl(entry) {
  if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
}

export const DocumentUploadSectionV5 = (props) => {
  const {
    multiple,
    module,
    moduleParam,
    parentCallback,
    onDocumentUploaded, // New callback for updating count
    required,
    disabled,
    iconOnly,
    buttonStyle = "default", // "default" or "compact"
    selectedFolder, // New prop for pre-selected folder
    onFolderChange, // New callback for folder changes
    openPickerRef,
    /** Optional ref; `.current(files)` adds File[]/FileList into the compact preview dialog */
    ingestFilesRef,
    hideTrigger = false,
    folderProjectId: folderProjectIdProp,
    layout = "preview",
  } = props;
  const { t } = useTranslation();
  const compact = layout === "compact";
  const [open, setOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [startUpload, setStartUpload] = useState(null);
  const fileInputRef = useRef(null);
  const uploadFunctionRef = useRef(null);
  const [resetUploader, setResetUploader] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(selectedFolder || null);
  const currentFolderRef = useRef(currentFolder);
  currentFolderRef.current = currentFolder;

  const folderProjectId =
    folderProjectIdProp || (module === "project" ? moduleParam : null);
  const { module: fallbackModule, moduleParam: fallbackParam } =
    folderUploadFallback(module, moduleParam, folderProjectId);

  const allowedFileTypes =
    props.allowedFileTypes ||
    "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

  // Sync folder from parent when the selected folder id changes (not object identity).
  useEffect(() => {
    setCurrentFolder(selectedFolder || null);
  }, [selectedFolder?.id]);

  const openPicker = useCallback(() => {
    if (!disabled) fileInputRef.current?.click();
  }, [disabled]);

  const ingestFiles = useCallback(
    (fileList) => {
      if (disabled) return;
      const selectedFiles = Array.from(fileList || []).filter(Boolean);
      if (!selectedFiles.length) return;

      const folder = currentFolderRef.current;
      setFilesToUpload((prev) => [
        ...prev,
        ...selectedFiles.map((file) =>
          bindUploadEntryToFolder(
            {
              file,
              previewUrl: compact ? null : URL.createObjectURL(file),
              title: "",
              module,
              moduleParam,
            },
            folder,
            fallbackModule,
            fallbackParam
          )
        ),
      ]);
      setOpen(true);
    },
    [
      disabled,
      compact,
      module,
      moduleParam,
      fallbackModule,
      fallbackParam,
    ]
  );

  useEffect(() => {
    if (!openPickerRef) return undefined;
    openPickerRef.current = disabled ? null : openPicker;
    return () => {
      openPickerRef.current = null;
    };
  }, [openPickerRef, openPicker, disabled]);

  useEffect(() => {
    if (!ingestFilesRef) return undefined;
    ingestFilesRef.current = disabled ? null : ingestFiles;
    return () => {
      ingestFilesRef.current = null;
    };
  }, [ingestFilesRef, ingestFiles, disabled]);

  const handleFolderChange = (event) => {
    const folder = folderFromSelectorEvent(event);
    setCurrentFolder(folder);
    setFilesToUpload((prev) =>
      prev.map((entry) =>
        bindUploadEntryToFolder(entry, folder, fallbackModule, fallbackParam)
      )
    );
    if (onFolderChange) {
      onFolderChange(folder);
    }
  };

  const handleFileSelection = (event) => {
    ingestFiles(event.target.files);
    event.target.value = "";
  };

  const handleTitleChange = (index, title) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].title = title;
      return updated;
    });
  };

  const handleDeleteFile = (indexToDelete) => {
    setFilesToUpload((prev) => {
      const removed = prev[indexToDelete];
      revokePreviewUrl(removed);
      const updated = prev.filter((_, index) => index !== indexToDelete);
      if (updated.length === 0) {
        setOpen(false);
      }
      return updated;
    });
  };

  const handleUploadComplete = (uploadedFiles) => {
    setFilesToUpload([]);
    setIsUploading(false);
    if (parentCallback) {
      parentCallback(uploadedFiles);
    }
    // Call the new callback to update document count
    if (onDocumentUploaded) {
      onDocumentUploaded(uploadedFiles.length);
    }
    handleClose();
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => {
      const newProgress = { ...prev, ...progress };
      const totalProgress = Object.values(newProgress).reduce(
        (acc, val) => acc + val,
        0
      );
      return newProgress;
    });
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  const handleClose = () => {
    setFilesToUpload((prev) => {
      prev.forEach(revokePreviewUrl);
      return [];
    });
    setOpen(false);
    setIsUploading(false);
    setUploadedCount(0);
    setUploadProgress({});
    uploadFunctionRef.current = null;
    setStartUpload(null);
    setCurrentFolder(selectedFolder || null);

    if (resetUploader) {
      resetUploader();
    }
  };

  // Compact button style for checklist items
  const renderCompactButton = () => (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={disabled}
      className="p-1.5 text-gray-400 hover:text-purple-600 transition-colors duration-200 rounded hover:bg-purple-50 border border-blue-200 bg-blue-50"
      title="Upload Pictures"
    >
      <CameraIcon className="h-4 w-4" />
    </button>
  );

  // Default button style (original)
  const renderDefaultButton = () => (
    <div
      className={TWclassNames(
        required ? "border-2 border-red-400" : "",
        "text-center bg-blue-400 hover:bg-blue-600 border-gray-300 px-5 py-5"
      )}
    >
      <label
        htmlFor={"file-upload"}
        className={TWclassNames(
          disabled ? "cursor-not-allowed" : "bg-white",
          "relative cursor-pointer rounded-md font-medium text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2"
        )}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-full flex flex-row items-center justify-center"
        >
          <CameraIcon className="h-5 w-6 mr-2 text-white" />
          {!iconOnly && (
            <div className="flex-row">
              <span>Last opp dokument/ bilde</span>
            </div>
          )}
        </button>
      </label>
    </div>
  );

  const overallProgress = filesToUpload.length
    ? Math.round(
        filesToUpload.reduce(
          (acc, _, index) =>
            acc +
            rowUploadProgress(index, uploadedCount, isUploading, uploadProgress),
          0
        ) / filesToUpload.length
      )
    : 0;

  const startUploadClick = () => {
    if (!isUploading && typeof uploadFunctionRef.current === "function") {
      setIsUploading(true);
      setUploadedCount(0);
      setUploadProgress({});
      uploadFunctionRef.current().then(() => setIsUploading(false));
    } else {
      console.error(
        "startUpload is not a function or is undefined:",
        uploadFunctionRef.current
      );
    }
  };

  const folderPicker = folderProjectId ? (
    <div className={compact ? "mb-3 rounded-lg bg-slate-50 p-3" : "mb-4 p-4 bg-blue-50 rounded-lg"}>
      <div className="flex items-center mb-2">
        <FolderIcon className={compact ? "h-4 w-4 text-slate-600 mr-2" : "h-5 w-5 text-blue-600 mr-2"} />
        <span className={compact ? "text-xs font-medium text-slate-700" : "text-sm font-medium text-blue-800"}>
          Mappe:
        </span>
      </div>
      <ProjectFolderSelector
        projectId={folderProjectId}
        selected={currentFolder}
        onChange={handleFolderChange}
      />
      {currentFolder && (
        <p className={compact ? "text-xs text-slate-500 mt-1" : "text-xs text-blue-600 mt-1"}>
          Dokumenter vil bli lastet opp til: {currentFolder.displayTitle || "Valgt mappe"}
        </p>
      )}
    </div>
  ) : null;

  return (
    <>
      {/* Hidden File Input */}
      <input
        accept={allowedFileTypes}
        type="file"
        multiple={multiple}
        ref={fileInputRef}
        className="sr-only"
        onChange={handleFileSelection}
      />

      {/* File Picker Button */}
      {!hideTrigger &&
        (buttonStyle === "compact"
          ? renderCompactButton()
          : renderDefaultButton())}

      <PMDialog
        open={open}
        onClose={() => setOpen(false)}
        title={
          compact
            ? t("document_archive.upload_dialog_title")
            : "Forhåndsvisning og opplasting"
        }
        size={compact ? "lg" : "2xl"}
        dense={compact}
      >
        {filesToUpload.length > 0 && (
          <>
            {folderPicker}

            {compact ? (
              <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-0.5">
                {filesToUpload.map((file, index) => {
                  const progress = rowUploadProgress(
                    index,
                    uploadedCount,
                    isUploading,
                    uploadProgress
                  );
                  const filename = file.file?.name || "";
                  return (
                    <div
                      key={`${filename}-${index}`}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-6 min-w-[2.4rem] items-center justify-center rounded bg-slate-100 px-1 text-[10px] font-semibold tracking-wide text-slate-600">
                          {fileExtLabel(filename)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                              {filename}
                            </p>
                            <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                              {formatUploadBytes(file.file?.size)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteFile(index)}
                              disabled={isUploading}
                              className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                              title={t("document_archive.upload_remove")}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={file.title}
                            disabled={isUploading}
                            placeholder={t("document_archive.upload_title_placeholder")}
                            onChange={(e) => handleTitleChange(index, e.target.value)}
                            className="mt-1 w-full border-0 border-b border-slate-200 bg-transparent px-0 py-0.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-[#0F766E] focus:outline-none focus:ring-0 disabled:opacity-60"
                          />
                          {isUploading ? (
                            <div className="mt-1.5 flex items-center gap-2">
                              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                                <div
                                  className="h-full rounded-full bg-[#0F766E] transition-[width]"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="w-8 text-right text-[10px] tabular-nums text-slate-500">
                                {progress}%
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              filesToUpload.map((file, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative mb-3"
                >
                  <button
                    type="button"
                    onClick={() => handleDeleteFile(index)}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 transition-colors duration-200 z-10"
                    title="Fjern bilde"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>

                  <img
                    src={
                      file.file.type === "application/pdf"
                        ? PdfPlaceholder
                        : file.previewUrl
                    }
                    className="h-40 object-cover rounded"
                    alt="Preview"
                  />
                  <PMField
                    name="title"
                    type="text"
                    label="Tittel"
                    value={file.title}
                    onChange={(e) => handleTitleChange(index, e.target.value)}
                  />
                </div>
              ))
            )}

            {isUploading && filesToUpload.length > 0 ? (
              compact ? (
                <div className="mt-3">
                  <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                    <span>{t("document_archive.upload_overall")}</span>
                    <span className="tabular-nums">{overallProgress}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-[#0F766E] transition-[width]"
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-center text-xs text-slate-600">
                    {t("document_archive.upload_status", {
                      current: Math.min(uploadedCount + 1, filesToUpload.length),
                      total: filesToUpload.length,
                    })}
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-4 w-full bg-gray-300 rounded h-4">
                    <div
                      className="h-full bg-blue-500 rounded"
                      style={{
                        width: `${
                          (Object.values(uploadProgress).reduce(
                            (acc, val) => acc + val,
                            0
                          ) /
                            (filesToUpload.length * 100)) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                  <p className="text-center mt-2 text-sm text-gray-700">
                    Opplasting {uploadedCount + 1} av {filesToUpload.length}
                  </p>
                </>
              )
            ) : null}

            {uploadedCount === filesToUpload.length && isUploading ? (
              <div className="flex justify-center items-center mt-4 text-green-500">
                <CheckCircleIcon className="h-6 w-6" />
                <span className="ml-2 text-green-600 font-semibold">
                  {compact
                    ? t("document_archive.upload_done")
                    : "Opplasting fullført"}
                </span>
              </div>
            ) : null}

            <button
              type="button"
              disabled={isUploading}
              className={
                compact
                  ? "mt-3 flex w-full items-center justify-center rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  : "mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded"
              }
              onClick={handleAddMoreFiles}
            >
              <CameraIcon className="h-5 w-5 mr-2" />
              {compact
                ? t("document_archive.upload_add_more")
                : "Legg til flere bilder"}
            </button>

            <div className="flex justify-between mt-4">
              <button
                className={
                  compact
                    ? "rounded-lg bg-slate-600 px-4 py-2 text-sm text-white"
                    : "w-20 bg-gray-600 text-white p-2 rounded"
                }
                onClick={() => handleClose()}
              >
                {compact ? t("document_archive.upload_close") : "Steng"}
              </button>

              <button
                disabled={isUploading || !uploadFunctionRef.current}
                className={
                  compact
                    ? "rounded-lg bg-[#0F766E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B5A54] disabled:bg-gray-400"
                    : "w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400"
                }
                onClick={startUploadClick}
              >
                {isUploading
                  ? compact
                    ? t("document_archive.upload_uploading")
                    : "Laster opp..."
                  : compact
                    ? t("document_archive.upload_start")
                    : "Last opp"}
              </button>
            </div>
          </>
        )}
      </PMDialog>

      {/* Upload Handler */}
      <UploaderV4
        filesToUpload={filesToUpload}
        onUploadStart={useCallback((uploadFn) => {
          if (uploadFn && typeof uploadFn === "function") {
            uploadFunctionRef.current = uploadFn;
            setStartUpload(true); // Set to true to indicate upload function is ready
          } else {
            console.error("Invalid upload function received:", uploadFn);
          }
        }, [])}
        onUploadComplete={handleUploadComplete}
        onProgressChange={handleProgressChange}
        onFileUploadComplete={handleFileUploadComplete}
        onReset={setResetUploader}
      />
    </>
  );
};
